import React from "react";
import { Message } from "@/types";
import { FileBadge, UploadFileStatus } from "./fileUpload";

interface UserMessageProps {
  message: Message;
}

/**
 * Strip the diagram-diff framing block prepended by the agent from the
 * user's chat message before rendering. The agent injects this block at
 * the very top of the user prompt for LLM consumption, but it must not be
 * shown back to the user. The block has the shape:
 *
 *   \n\n---\nDIAGRAM (CHANGES|STATE) ...\n\n<body>\n---\n
 *
 * followed by the user's actual typed text. We match leading whitespace,
 * the opening fence, the recognized header, the body, and the closing
 * fence — and drop them. If no recognized framing is present, the message
 * passes through unchanged.
 */
const DIAGRAM_FRAMING_PATTERN =
  /^[ \t]*---[ \t]*\nDIAGRAM (?:CHANGES|STATE)\b[^\n]*\n[\s\S]*?\n---[ \t]*\n+/gm;

/**
 * Strip the attachments framing block the agent prepends to the user prompt
 * when files are sent with a message. Like the diagram framing, this is for
 * LLM consumption only and must not be shown back to the user (the attachments
 * are rendered separately as badges). The block has the shape:
 *
 *   ---\nFILES ATTACHED TO THIS MESSAGE\n...<lines>...\n---\n
 *
 * It always appears at the very top of the prompt (above any diagram framing),
 * so we strip it first.
 */
const ATTACHMENTS_FRAMING_PATTERN =
  /^[ \t]*---[ \t]*\nFILES ATTACHED TO THIS MESSAGE\b[\s\S]*?\n---[ \t]*\n+/gm;

/**
 * Strip the customer-context framing block the agent prepends when the project
 * has Salesforce IDs recorded on it. Like the others this is for LLM consumption
 * only — the IDs are shown in the Overview's Customer box, not the transcript.
 *
 *   ---\nCUSTOMER ALREADY LINKED TO THIS PROJECT\n...<lines>...\n---\n
 *
 * Sits above the attachments block (see `app.py`), so it is stripped first.
 */
const CUSTOMER_CONTEXT_FRAMING_PATTERN =
  /^[ \t]*---[ \t]*\nCUSTOMER ALREADY LINKED TO THIS PROJECT\b[\s\S]*?\n---[ \t]*\n+/gm;

/**
 * Remove every agent-injected framing block from a user message.
 *
 * Patterns are line-anchored (`m`) and global rather than anchored to the start
 * of the string, because the agent stacks these blocks and can prepend others
 * above them — `app.py` puts an indexing notice on top when content indexing
 * fails. A start-of-string anchor only matched whichever block happened to be
 * outermost, so the rest leaked into the chat bubble verbatim.
 */
const stripAgentFraming = (text: string): string => {
  return text
    .replace(CUSTOMER_CONTEXT_FRAMING_PATTERN, "")
    .replace(ATTACHMENTS_FRAMING_PATTERN, "")
    .replace(DIAGRAM_FRAMING_PATTERN, "")
    .trimStart();
};

const UserMessage = ({ message }: UserMessageProps) => {
  const displayContent = stripAgentFraming(message.content);
  const attachments = message.attachments ?? [];
  return (
    <div className="flex justify-end mb-16">
      <div className="max-w-3xl">
        <div className="bg-blue-50 dark:bg-[#1F273C] rounded-lg px-4 py-3">
          <div className="text-sm whitespace-pre-wrap text-gray-900 dark:text-white">
            {displayContent}
          </div>
          {/* Read-only badges for any files sent with this message. */}
          {attachments.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {attachments.map((file) => (
                <FileBadge
                  key={file.s3Key}
                  file={{
                    id: file.s3Key,
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    status: UploadFileStatus.READY,
                    s3Key: file.s3Key,
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserMessage;
